import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ResponsiveDialog from '../common/ResponsiveDialog';
import apiService from '../../services/api.service';
import { GL_ACCOUNT_FIELDS, keptWarehouseKeys, type GLAccounts, type GLOverride, type GLPreview, type GLRow } from '../../types/itemRequests';

/**
 * The G/L accounts SAP's advanced rules give the item, per warehouse type (and
 * per warehouse that has a rule of its own). Read-only for the initiator; the
 * Finance reviewer can set accounts for this item only, row by row.
 */

const STATUS: Record<GLRow['status'], { label: string; color: 'success' | 'warning' | 'error' }> = {
  complete: { label: 'Complete', color: 'success' },
  partial: { label: 'No cost account', color: 'warning' },
  none: { label: 'No rule', color: 'error' },
};

interface Props {
  preview: GLPreview | null;
  loading?: boolean;
  error?: string | null;
  emptyHint?: string;
  overrides?: Record<string, GLOverride>;
  editable?: boolean;
  companyId?: number;
  onOverrideChange?: (row: GLRow, accounts: GLAccounts | null) => void;
  /** Warehouse rows that keep their own accounts for this item; worked out from the rows when not given. */
  keptKeys?: string[];
}

function AccountCell({ code, name }: { code: string | null | undefined; name?: string | null }) {
  if (!code) return <Typography variant="body2" color="text.disabled">None</Typography>;
  return (
    <Box sx={{ minWidth: 150 }}>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>{code}</Typography>
      {name ? <Typography variant="caption" color="text.secondary" display="block">{name}</Typography> : null}
    </Box>
  );
}

/** An account code with a search of SAP's chart of accounts (code or name). */
function AccountField({ companyId, label, value, onChange, names, onName }: {
  companyId: number; label: string; value: string; onChange: (v: string) => void;
  names: Record<string, string | null>; onName: (code: string, name: string) => void;
}) {
  const [input, setInput] = useState(value);
  const [options, setOptions] = useState<Array<{ code: string; name: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState('');
  useEffect(() => setInput(value), [value]);
  useEffect(() => {
    const term = input.trim();
    if (term.length < 2 || (term === value && names[value])) { setOptions([]); setSearched(''); return undefined; }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(() => {
      apiService.searchItemRequestAccounts(companyId, term)
        .then((res: any) => { if (!cancelled) { setOptions(Array.isArray(res?.data) ? res.data : []); setSearched(term); } })
        .catch(() => { if (!cancelled) { setOptions([]); setSearched(''); } })
        .finally(() => { if (!cancelled) setBusy(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); setBusy(false); };
  }, [companyId, input, value, names]);
  const noMatch = !busy && !!searched && searched === input.trim() && options.length === 0;
  return (
    <Autocomplete
      freeSolo
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.code)}
      renderOption={(props, o) => (
        <li {...props} key={o.code}>
          <Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{o.code}</Typography>
            <Typography variant="caption" color="text.secondary">{o.name}</Typography>
          </Box>
        </li>
      )}
      inputValue={input}
      onInputChange={(_e, v, reason) => {
        setInput(v);
        if (reason !== 'reset') onChange(v.trim());
      }}
      onChange={(_e, v) => {
        if (v && typeof v !== 'string') {
          onName(v.code, v.name);
          onChange(v.code);
          setInput(v.code);
        }
      }}
      loading={busy}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          error={noMatch}
          helperText={noMatch ? 'No account matches. Search by account code or by a word in its name.' : (value ? (names[value] || ' ') : ' ')}
          InputProps={{ ...params.InputProps, endAdornment: <>{busy ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }}
        />
      )}
    />
  );
}

export default function GLDeterminationTable({
  preview, loading, error, emptyHint, overrides = {}, editable = false, companyId = 0, onOverrideChange, keptKeys,
}: Props) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<GLRow | null>(null);
  const [draft, setDraft] = useState<GLAccounts>({});
  const [extraNames, setExtraNames] = useState<Record<string, string | null>>({});
  const names = useMemo(() => ({ ...(preview?.accountNames || {}), ...extraNames }), [preview, extraNames]);

  if (loading) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }} role="status">
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Reading SAP&apos;s G/L account rules…</Typography>
      </Stack>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!preview) return <Typography variant="body2" color="text.secondary">{emptyHint || 'Choose the Item Group and Classification to see the G/L accounts.'}</Typography>;

  const effective = (row: GLRow): GLAccounts => overrides[row.key]?.accounts ?? row.accounts;
  const incomplete = preview.rows.filter((r) => !overrides[r.key] && r.status !== 'complete');
  const kept = new Set(keptKeys ?? keptWarehouseKeys(preview.rows, overrides));
  // A warehouse type's warehouses with a rule of their own (the editor names them).
  const ownRuleWarehouses = (row: GLRow | null): GLRow[] => {
    const type = row && !row.warehouseCode ? (row.warehouseType || '').toUpperCase() : '';
    return type ? preview.rows.filter((r) => r.key.startsWith('whs:') && (r.warehouseType || '').toUpperCase() === type) : [];
  };

  const openEditor = (row: GLRow) => {
    const base = effective(row);
    const start: GLAccounts = {};
    for (const { field } of GL_ACCOUNT_FIELDS) start[field] = base[field] ?? null;
    setDraft(start);
    setEditing(row);
  };
  const saveEditor = () => {
    if (editing && onOverrideChange) {
      const cleaned: GLAccounts = {};
      for (const { field } of GL_ACCOUNT_FIELDS) cleaned[field] = String(draft[field] || '').trim() || null;
      onOverrideChange(editing, cleaned);
    }
    setEditing(null);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        SAP rules of financial year {preview.period}. Each warehouse type picks its rule when the item is received or issued there.
      </Typography>
      {incomplete.length > 0 && (
        <Alert severity="warning">
          {incomplete.length === 1 ? 'One warehouse type has' : `${incomplete.length} warehouse types have`} no complete rule for this item:
          {' '}{incomplete.map((r) => r.label).join(', ')}. Postings that need a cost account would fail there unless Finance sets accounts for this item.
        </Alert>
      )}
      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
        <Table size="small" aria-label="G/L accounts per warehouse type">
          <TableHead>
            <TableRow>
              <TableCell>Warehouse type</TableCell>
              <TableCell>SAP rule</TableCell>
              <TableCell>Inventory account</TableCell>
              <TableCell>Cost account</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sx={{ width: 96 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {preview.rows.map((row) => {
              const acc = effective(row);
              const own = !!overrides[row.key];
              const open = !!expanded[row.key];
              return (
                <React.Fragment key={row.key}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.label}</Typography>
                      {row.key.startsWith('type:') && row.warehouseCount > 0 && (
                        <Typography variant="caption" color="text.secondary">{row.warehouseCount} warehouse{row.warehouseCount === 1 ? '' : 's'}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {own ? <Chip size="small" color="primary" variant="outlined" label="Set for this item" />
                        : row.rule ? (
                          <Box>
                            <Typography variant="body2">{row.rule.code}{row.rule.description ? ` · ${row.rule.description}` : ''}</Typography>
                            {row.rule.itemSpecific && <Typography variant="caption" color="text.secondary">Rule for this item</Typography>}
                            {kept.has(row.key) && (
                              <Typography variant="caption" color="text.secondary" display="block">Keeps these accounts for this item</Typography>
                            )}
                          </Box>
                        ) : <Typography variant="body2" color="text.disabled">None</Typography>}
                    </TableCell>
                    <TableCell><AccountCell code={acc.InventoryAccount} name={acc.InventoryAccount ? names[acc.InventoryAccount] : null} /></TableCell>
                    <TableCell><AccountCell code={acc.CostAccount} name={acc.CostAccount ? names[acc.CostAccount] : null} /></TableCell>
                    <TableCell>
                      {own ? <Chip size="small" color="success" label="Set by Finance" />
                        : <Chip size="small" color={STATUS[row.status].color} label={STATUS[row.status].label} />}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={open ? 'Hide all accounts' : 'Show all accounts'}>
                        <IconButton size="small" aria-label={open ? `Hide all accounts of ${row.label}` : `Show all accounts of ${row.label}`}
                          onClick={() => setExpanded((e) => ({ ...e, [row.key]: !open }))}>
                          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      {editable && (
                        <Tooltip title="Set accounts for this item">
                          <IconButton size="small" aria-label={`Set accounts for this item in ${row.label}`} onClick={() => openEditor(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  {open && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ bgcolor: 'action.hover' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.5 }}>
                          {GL_ACCOUNT_FIELDS.map(({ field, label }) => (
                            <Box key={field} sx={{ minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <AccountCell code={acc[field]} name={acc[field] ? names[acc[field] as string] : null} />
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {preview.conditionalRules > 0 && (
        <Typography variant="caption" color="text.secondary">
          {preview.conditionalRules} rule{preview.conditionalRules === 1 ? '' : 's'} of this year also depend on the vendor or document and are not shown here.
        </Typography>
      )}

      <ResponsiveDialog open={!!editing} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>Accounts for this item · {editing?.label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            On final approval PRPO adds an SAP rule for this item only, in {editing?.warehouseCode ? `warehouse ${editing.warehouseCode}` : `${editing?.label} warehouses`}, with these accounts.
          </Typography>
          {ownRuleWarehouses(editing).length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {ownRuleWarehouses(editing).map((r) => r.label).join(', ')}
              {ownRuleWarehouses(editing).length === 1 ? ' has a rule of its own and keeps its accounts' : ' have rules of their own and keep their accounts'}
              {' '}for this item: PRPO adds an item rule there too. To change them, edit {ownRuleWarehouses(editing).length === 1 ? 'its row' : 'their rows'}.
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {GL_ACCOUNT_FIELDS.map(({ field, label }) => (
              <AccountField
                key={field}
                companyId={companyId}
                label={label}
                value={String(draft[field] || '')}
                onChange={(v) => setDraft((d) => ({ ...d, [field]: v }))}
                names={names}
                onName={(code, name) => setExtraNames((n) => ({ ...n, [code]: name }))}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          {editing && overrides[editing.key] && (
            <Button color="inherit" onClick={() => { if (editing && onOverrideChange) onOverrideChange(editing, null); setEditing(null); }}>
              Use SAP&apos;s rule again
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEditor}>Use these accounts</Button>
        </DialogActions>
      </ResponsiveDialog>
    </Stack>
  );
}
